import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import { ArrowRightIcon, LoaderCircleIcon } from 'lucide-react'
import { useState } from 'react'

import { useEmbeddedOnboarding } from 'components/hooks/employer_onboarding/useEmbeddedOnboarding'
import { useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { useCensus } from 'pages/employer_onboarding/CensusProvider'
import { useQuote } from 'pages/employer_onboarding/QuoteProvider'
import { Banner, Button, SolidCircleCheckIcon, usePageTitle } from 'slab'
import {
  getGetQuotingProspectsIdQueryKey,
  useGetQuotingProspectsProspectIdCensusCensusIdEmployees as useGetCensusEmployees,
  usePatchQuotingProspectsProspectIdQuotesId as useUpdateQuote,
  usePostQuotingProspectsProspectIdQuotes as useCreateQuote,
} from 'utils/web_api'

import CensusEmployeesTable from './CensusEmployeesTable'
import { CenteredLayout } from '../../CenteredLayout'
import { useEmployer } from '../../EmployerProvider'

const PAGE_SIZE = 50

export function CensusConfirmationPage () {
  usePageTitle('Confirm your team')

  const queryClient = useQueryClient()
  const { isEmbedded } = useEmbeddedOnboarding()
  const { employer } = useEmployer()
  const { census } = useCensus()
  const { quote, refetch: refetchQuote } = useQuote()
  const { navigateToPreviousStep, navigateToNextStep } = useOnboardingStep()

  const [pageIndex, setPageIndex] = useState(0)

  const {
    data: employees,
    isLoading,
    error,
  } = useGetCensusEmployees(
    employer?.quoting_prospect_id ?? '',
    census?.id ?? '',
    {
      'page[size]': PAGE_SIZE,
      'page[number]': pageIndex + 1,
    },
    {
      query: {
        placeholderData: keepPreviousData,
        enabled: census?.status === 'processed',
      },
    }
  )

  const createQuoteMutation = useCreateQuote({
    mutation: {
      onSuccess: async () => {
        await refetchQuote()
        await queryClient.invalidateQueries({
          queryKey: getGetQuotingProspectsIdQueryKey(employer?.quoting_prospect_id ?? ''),
        })

        navigateToNextStep()
      },
      onError: () => {
        // TODO: Handle error
      },
    },
  })

  const updateQuoteMutation = useUpdateQuote({
    mutation: {
      onSuccess: async () => {
        await refetchQuote()
        await queryClient.invalidateQueries({
          queryKey: getGetQuotingProspectsIdQueryKey(employer?.quoting_prospect_id ?? ''),
        })

        navigateToNextStep()
      },
      onError: () => {
        // TODO: Handle error
      },
    },
  })

  function handleCreateQuote () {
    if (quote) {
      updateQuoteMutation.mutate({
        prospectId: employer?.quoting_prospect_id ?? '',
        id: quote.id,
        data: {
          quoting_prospect_employee_census_id: census?.id ?? '',
        },
      })
    } else {
      createQuoteMutation.mutate({
        prospectId: employer?.quoting_prospect_id ?? '',
        data: {
          quoting_prospect_employee_census_id: census?.id ?? '',
          quote_type: 'self_serve_tool',
          name: `Self-serve quote for ${employer?.name}`,
        },
      })
    }
  }

  function renderContent () {
    if (!census) {
      return (
        <div className='p-10 text-center'>
          <div className='heading-sm text-pretty'>
            We don't have a census for you at the moment. Please go back and
            upload a new census.
          </div>
        </div>
      )
    }

    if (census?.status === 'failed') {
      return (
        <div className='p-10 text-center'>
          <div className='heading-sm text-pretty'>
            Your census failed to process. Please go back and upload a new
            census.
          </div>
        </div>
      )
    }

    if (census?.status === 'processing' || census?.status === 'unprocessed') {
      return (
        <div className='flex flex-col items-center justify-center gap-2 h-full p-10'>
          <LoaderCircleIcon size={24} className='animate-spin text-gray-500' />
          <div className='text-sm text-gray-500'>Processing census...</div>
        </div>
      )
    }

    if (isLoading) {
      return (
        <div className='flex flex-col items-center justify-center gap-2 h-full p-10'>
          <LoaderCircleIcon size={24} className='animate-spin text-gray-500' />
          <div className='text-sm text-gray-500'>Loading employees...</div>
        </div>
      )
    }

    if (error) {
      return (
        <div className='p-10'>
          <Banner type='critical' title='Error loading employees'>
            {error instanceof Error && error.message}
          </Banner>
        </div>
      )
    }

    return (
      <div>
        <div className='bg-gray-50 p-4 rounded-t-2xl'>
          <div className='flex items-center gap-2'>
            <SolidCircleCheckIcon size={16} className='text-green-400' />
            <div>
              {employees?.pagination?.total_records} employee{employees?.pagination?.total_records === 1 ? '' : 's'} {census.format === 'manual_entry' ? 'added' : 'imported from census'}
            </div>
          </div>
        </div>
        <CensusEmployeesTable
          census={census}
          employees={employees?.data ?? []}
          totalEmployees={employees?.pagination?.total_records ?? 0}
          pageSize={PAGE_SIZE}
          pageIndex={pageIndex}
          onPageChange={setPageIndex}
        />
      </div>
    )
  }

  const heading = isEmbedded ? "Thanks! Here's what we have:" : "Thanks! Here's what you've given us:"

  return (
    <CenteredLayout displaySupportLink>
      <CenteredLayout.Header
        heading={heading}
        subheading='Take a look and confirm everything looks right. You can also select which employees are ineligible to enroll in health insurance to more accurately predict your costs.'
      />
      <CenteredLayout.Container padded={false}>
        {renderContent()}
      </CenteredLayout.Container>
      <CenteredLayout.Buttons>
        <Button variant='secondary' onClick={navigateToPreviousStep}>
          Back
        </Button>
        <Button
          variant='primary'
          disabled={
            census?.status !== 'processed' || createQuoteMutation.isPending
          }
          onClick={() => {
            handleCreateQuote()
          }}
          rightIcon={ArrowRightIcon}
        >
          Looks good to me!
        </Button>
      </CenteredLayout.Buttons>
    </CenteredLayout>
  )
}
